import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BrandMark } from "@/components/shared/BrandMark";
import { AttentionDot } from "@/components/shared/AttentionDot";
import { CreditBadge, LiveMoneyMeter } from "@/components/layout/CreditBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  LayoutDashboard,
  Package,
  FolderOpen,
  Send,
  Radio,
  MessageSquare,
  Users,
  ShieldAlert,
  BarChart3,
  RefreshCcw,
  Compass,
  UserPlus,
  ShoppingBag,
  Radar,
  Search,
  Sparkles,
  CalendarDays,
  ExternalLink,
  Gift,
  Wand2,
  Zap,
  Waves,
  Rocket,
  ShoppingCart,
  MessageCircle,
  MessageSquareText,
  Megaphone,
  Target,
  Link2,
  Store,
  Truck,
  Archive,
  Wallet,
  Shield,
  Sword,
  Heart,
  BarChart4,
  Users2,
  Trophy,
  Handshake,
  ImageIcon,
  PenSquare,
  Blocks,
  Coins,
  LifeBuoy,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";

const COLLAPSE_STORAGE_KEY = "sb_sidebar_collapsed";

/**
 * Global sidebar-collapsed state, persisted to localStorage.
 * Ctrl+B (Cmd+B on macOS) toggles it from anywhere in the app.
 *
 * The updater passed to setState is intentionally PURE — no localStorage
 * writes, no window events. React 18 StrictMode invokes state updaters
 * twice in development to help detect impurity; any side-effect in there
 * would run twice per call and silently no-op the toggle (this bug was
 * caught in iteration_153). Persistence lives in a separate effect keyed
 * on `collapsed`.
 */
export function useSidebarCollapsed(): [boolean, (next?: boolean) => void] {
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1"; }
    catch { return false; }
  });

  // Persist whenever the state changes. StrictMode-safe because setItem
  // is idempotent — writing the same value twice has no visible effect.
  React.useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  const set = React.useCallback((next?: boolean) => {
    setCollapsed((prev) => (typeof next === "boolean" ? next : !prev));
  }, []);

  // Global Ctrl+B / ⌘+B shortcut to toggle the sidebar.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key !== "b" && e.key !== "B") return;
      // Skip when the user is typing in an input/textarea/contenteditable.
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      e.preventDefault();
      set();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [set]);

  return [collapsed, set];
}

type NavItem = {
  to: string;
  label: string;
  icon: any;
  tid: string;
  external?: boolean;
  roles?: readonly string[];
  /** When true, renders a small pulsing dot next to the label so
      flagship or free-to-use surfaces catch the eye in the sidebar. */
  attention?: boolean;
};

type NavSection = {
  label: string;
  /** Tailwind color hue used to tint this section's icons in the sidebar.
   * The active row always overrides this with saffron. */
  color:
    | "saffron"
    | "fuchsia"
    | "emerald"
    | "sky"
    | "slate";
  items: NavItem[];
};

/** Section-color → concrete Tailwind classes. Kept as static string literals
 *  so Tailwind's JIT compiler picks them up. */
const SECTION_ICON_COLOR: Record<NavSection["color"], string> = {
  saffron: "text-[color:var(--sb-saffron,#F97316)]",
  fuchsia: "text-fuchsia-500",
  emerald: "text-emerald-500",
  sky: "text-sky-500",
  slate: "text-slate-500 dark:text-slate-400",
};

/**
 * Primary sidebar navigation — consolidated per the world-class UX blueprint
 * (see /app/design_guidelines.json). We ship 5 curated groups with sticky
 * headers instead of 14 collapsible accordions. Every previous menu item
 * is preserved — they've just been re-grouped so the sections are meatier
 * and the list scans as one continuous "bird's-eye view".
 *
 * External items open in a new tab. Account/Admin surfaces live in the
 * TopBar profile dropdown, not here.
 */
const NAV_SECTIONS: NavSection[] = [
  {
    label: "Workspace",
    color: "saffron",
    items: [
      { to: "/dashboard", label: "Overview", icon: LayoutDashboard, tid: "nav-overview" },
      { to: "/discovery", label: "Discovery", icon: Compass, tid: "nav-discovery" },
      { to: "/ai-cart", label: "AI Cart™", icon: ShoppingCart, tid: "nav-ai-cart", attention: true },
      { to: "/workspace/ai/signal-to-action", label: "Signal → Action", icon: Zap, tid: "nav-signal-action" },
      { to: "/workspace/ai/money-meter", label: "Money Meter", icon: Wand2, tid: "nav-money-meter" },
      { to: "/workspace/ai/viral-waves", label: "Viral Waves", icon: Waves, tid: "nav-viral-waves" },
      { to: "/workspace/ai/autopilot", label: "AutoPilot", icon: Rocket, tid: "nav-autopilot" },
      { to: "/workspace/intent-radar", label: "Intent Radar", icon: Radar, tid: "nav-intent-radar" },
      { to: "/workspace/ai/brand-studio", label: "Brand Studio", icon: ImageIcon, tid: "nav-brand-studio" },
      { to: "/workspace/ai/blog", label: "AI Blog", icon: PenSquare, tid: "nav-ai-blog" },
    ],
  },
  {
    label: "Content & Social",
    color: "fuchsia",
    items: [
      { to: "/composer", label: "Composer", icon: Sparkles, tid: "nav-composer" },
      { to: "/calendar", label: "Calendar", icon: CalendarDays, tid: "nav-calendar" },
      { to: "/posts", label: "Posts", icon: Send, tid: "nav-posts" },
      { to: "/library", label: "Content Library", icon: FolderOpen, tid: "nav-library" },
      { to: "/comments", label: "Comments", icon: MessageSquare, tid: "nav-comments" },
      { to: "/leads", label: "Leads", icon: Users, tid: "nav-leads" },
      { to: "/lead-capture", label: "Lead Capture", icon: UserPlus, tid: "nav-lead-capture" },
      { to: "/complaints", label: "Complaints", icon: ShieldAlert, tid: "nav-complaints" },
    ],
  },
  {
    label: "Commerce",
    color: "emerald",
    items: [
      { to: "/products", label: "Products", icon: Package, tid: "nav-products" },
      { to: "/settings/shopify", label: "Shopify", icon: ShoppingBag, tid: "nav-shopify", roles: ["admin"] },
      { to: "/workspace/catalog/manual", label: "Add Manually", icon: PenSquare, tid: "nav-catalog-manual" },
      { to: "/workspace/catalog/bulk", label: "Bulk Upload", icon: Archive, tid: "nav-catalog-bulk" },
      { to: "/workspace/catalog/stores", label: "Connected Stores", icon: Store, tid: "nav-catalog-stores" },
      { to: "/workspace/catalog/collections", label: "Collections & Tags", icon: Blocks, tid: "nav-catalog-collections" },
      { to: "/workspace/catalog/media", label: "Media Library", icon: ImageIcon, tid: "nav-catalog-media" },
      { to: "/workspace/catalog/ai-copy", label: "AI Descriptions", icon: Sparkles, tid: "nav-catalog-ai-copy" },
      { to: "/workspace/sell/orders", label: "Orders", icon: Truck, tid: "nav-sell-orders" },
      { to: "/workspace/sell/inventory", label: "Inventory", icon: Archive, tid: "nav-sell-inventory" },
      { to: "/workspace/sell/returns", label: "Returns", icon: RefreshCcw, tid: "nav-sell-returns" },
      { to: "/workspace/sell/payouts", label: "Payouts", icon: Wallet, tid: "nav-sell-payouts" },
      { to: "/workspace/clients", label: "Agency Clients", icon: Users, tid: "nav-clients" },
    ],
  },
  {
    label: "Marketing & Intel",
    color: "sky",
    items: [
      { to: "/workspace/marketing/whatsapp", label: "WhatsApp", icon: MessageCircle, tid: "nav-mkt-whatsapp" },
      { to: "/workspace/marketing/sms", label: "Bulk SMS", icon: MessageSquareText, tid: "nav-mkt-sms" },
      { to: "/workspace/marketing/ads", label: "Ads Manager", icon: Target, tid: "nav-mkt-ads" },
      { to: "/workspace/marketing/broadcast", label: "Broadcasts", icon: Megaphone, tid: "nav-mkt-broadcast" },
      { to: "/workspace/marketing/launch", label: "Launch Buzz", icon: Rocket, tid: "nav-mkt-launch" },
      { to: "/workspace/marketing/shortener", label: "URL Shortener", icon: Link2, tid: "nav-mkt-shortener" },
      { to: "/workspace/brand/reputation", label: "Reputation", icon: Shield, tid: "nav-brand-reputation" },
      { to: "/workspace/brand/competitors", label: "Competitors", icon: Sword, tid: "nav-brand-competitors" },
      { to: "/workspace/brand/sentiment", label: "Sentiment", icon: Heart, tid: "nav-brand-sentiment" },
      { to: "/workspace/brand/insights", label: "Customer Insights", icon: Users2, tid: "nav-brand-insights" },
      { to: "/workspace/brand/history", label: "SibaScore History", icon: BarChart4, tid: "nav-brand-history" },
      { to: "/workspace/brand/campaigns", label: "Campaigns", icon: Megaphone, tid: "nav-brand-campaigns" },
      { to: "/workspace/creator/performance", label: "Performance", icon: BarChart4, tid: "nav-creator-performance" },
      { to: "/workspace/creator/audience", label: "Audience", icon: Users2, tid: "nav-creator-audience" },
      { to: "/workspace/creator/topics", label: "Trending Topics", icon: Trophy, tid: "nav-creator-topics" },
      { to: "/workspace/creator/collabs", label: "Collab Match", icon: Handshake, tid: "nav-creator-collabs" },
    ],
  },
  {
    label: "System & Growth",
    color: "slate",
    items: [
      { to: "/analytics", label: "Analytics", icon: BarChart3, tid: "nav-analytics" },
      { to: "/insights", label: "Insights", icon: BarChart3, tid: "nav-insights" },
      { to: "/platforms", label: "Platforms", icon: Radio, tid: "nav-platforms" },
      { to: "/retry", label: "Retry Center", icon: RefreshCcw, tid: "nav-retry" },
      { to: "/referrals", label: "Referrals", icon: Gift, tid: "nav-referrals" },
      { to: "/workspace/credits", label: "Credits & Billing", icon: Coins, tid: "nav-credits" },
      { to: "/workspace/support/help", label: "Help Center", icon: LifeBuoy, tid: "nav-support-help" },
    ],
  },
];

/**
 * SidebarNavContent — the inner content of the sidebar (logo + nav).
 * Rendered inside the desktop <aside> (h-screen sticky, may be collapsed to
 * an icon rail) and the mobile Sheet (h-full inside the drawer, always
 * expanded — mobile has room for full labels). The middle nav is the only
 * scrollable region; header stays pinned. There is intentionally no footer
 * — pressing Ctrl+K anywhere still opens the command palette.
 */
export const SidebarNavContent: React.FC<{
  onNavigate?: () => void;
  /** Desktop-only: renders the icon-rail variant with hidden labels. */
  collapsed?: boolean;
  /** When true (mobile drawer), render the Money Meter + SB Credits cards
   *  inside the sidebar. Desktop hides these — they now live in the TopBar
   *  as compact chips. */
  showWallet?: boolean;
}> = ({ onNavigate, collapsed = false, showWallet = false }) => {
  const { user } = useAuth();
  const role = user?.role || "user";
  const navRef = React.useRef<HTMLElement | null>(null);

  // Persist sidebar scroll position so navigating doesn't jump-scroll to top.
  React.useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem("sb_sidebar_scroll");
    if (saved) el.scrollTop = parseInt(saved, 10) || 0;
    const onScroll = () => sessionStorage.setItem("sb_sidebar_scroll", String(el.scrollTop));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="flex flex-col h-full min-h-0 bg-[color:var(--sg-card)]" data-testid="sidebar-inner">
      <div
        className={`${collapsed ? "px-2 py-3 flex justify-center" : "px-5 pt-5 pb-4"} border-b border-[color:var(--sg-border)] shrink-0`}
      >
        <Link
          to="/"
          onClick={onNavigate}
          className={collapsed ? "block" : "flex items-center gap-2.5 group"}
          data-testid="sidebar-logo"
          title={collapsed ? "SociBaba — go home" : undefined}
        >
          {collapsed ? (
            <BrandMark
              showIcon
              iconClassName="h-8 w-8 inline-block align-middle group-hover:scale-105 transition-transform dark:invert"
              className="sr-only"
            />
          ) : (
            <div>
              <BrandMark
                showIcon
                iconClassName="h-9 w-9 mr-2 inline-block align-middle group-hover:scale-105 transition-transform dark:invert"
                className="font-serif-display text-lg font-bold tracking-tight leading-none flex items-center"
              />
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--sg-muted)] mt-1">
                {role === "admin" ? "Admin" : role === "staff" ? "Staff · Read-only" : "Social Intelligence"}
              </div>
            </div>
          )}
        </Link>
      </div>
      {!collapsed && showWallet && (
        <>
          <div className="shrink-0">
            <LiveMoneyMeter />
            <CreditBadge />
          </div>
        </>
      )}
      {!collapsed && (
        <div className="px-3 pt-3 pb-2 shrink-0">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("sb:open-cmdk"))}
              data-testid="sidebar-cmdk-search"
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] text-[color:var(--sg-muted)] bg-[color:var(--sg-muted-bg)]/60 border border-[color:var(--sg-border)] hover:bg-[color:var(--sg-muted-bg)] hover:text-[color:var(--sg-fg)] transition-colors"
              aria-label="Search or jump to any page"
            >
              <Search className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
              <span className="flex-1 text-left">Search…</span>
              <kbd className="text-[9px] font-mono bg-[color:var(--sg-card)] border border-[color:var(--sg-border)] rounded px-1 py-0.5">
                {typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl+K"}
              </kbd>
            </button>
          </div>
      )}
      <NavList
        ref={navRef}
        collapsed={collapsed}
        role={role}
        onNavigate={onNavigate}
      />
    </div>
  );
};

/* -------------------------------------------------------------------------
 * NavList — flat, scannable, sticky-header primary navigation.
 *
 * Per design_guidelines.json we abandoned the 14-section accordion in
 * favour of a flat list under 5 sticky section headers. No expand/collapse
 * clicks required — the list is one continuous "bird's-eye view" and the
 * headers stay visible as you scroll, so the user always knows where they
 * are. Command palette (⌘K) handles the long tail.
 *
 * Collapsed rail (64px): headers hidden, icons only, shadcn Tooltip on
 * hover reveals labels — a proper Vercel/Linear-style icon rail.
 *
 * Active row: 2px saffron left-border + saffron/10 background tint +
 * saffron text + saffron icon.
 * ------------------------------------------------------------------------- */

interface NavListProps {
  collapsed: boolean;
  role: string;
  onNavigate?: () => void;
}

const NavList = React.forwardRef<HTMLElement, NavListProps>(
  ({ collapsed, role, onNavigate }, forwardedRef) => {
    const localRef = React.useRef<HTMLElement | null>(null);

    // Merge forwarded ref with local ref so the parent scroll-persistence
    // effect still works while we can also query the element internally.
    const setRef = (el: HTMLElement | null) => {
      localRef.current = el;
      if (typeof forwardedRef === "function") forwardedRef(el);
      else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLElement | null>).current = el;
    };

    const filterItem = (it: NavItem) => {
      if (it.roles && !it.roles.includes(role)) return false;
      return true;
    };

    return (
      <nav
        ref={setRef}
        data-testid="sidebar-nav"
        aria-label="Primary"
        className={`flex-1 min-h-0 ${collapsed ? "flex flex-col py-2 px-1 overflow-y-auto sb-scrollbar-thin" : "pb-3"}`}
      >
        {/* Collapsed rail: flatten every section into one flex column so all
            56 icons distribute EVENLY across the available viewport height.
            Each row becomes `flex-1 min-h-0`, meaning no scroll, no overflow.
            Section colours are preserved per icon. */}
        {collapsed && (
          <ul
            data-testid="nav-collapsed-rail"
            className="list-none flex flex-col flex-1 min-h-0 items-center"
          >
            {NAV_SECTIONS.flatMap((section) =>
              section.items.filter(filterItem).map((item) => (
                <li
                  key={item.to}
                  className="flex-1 min-h-[24px] w-full flex items-center justify-center"
                >
                  <NavRow
                    item={item}
                    collapsed
                    onNavigate={onNavigate}
                    sectionColor={section.color}
                  />
                </li>
              )),
            )}
          </ul>
        )}
        {!collapsed && NAV_SECTIONS.map((section) => {
          const visible = section.items.filter(filterItem);
          if (visible.length === 0) return null;

          // Expanded: sticky section header + flat items as direct nav
          // children (React.Fragment) so the sticky containing block is the
          // whole <nav> — headers stick against the page scroll, not a
          // 200px div.
          return (
            <React.Fragment key={section.label}>
              <div
                data-testid={`nav-section-${section.label.toLowerCase()}`}
                className="sticky top-0 z-10 px-4 pt-4 pb-1.5 text-[10px] uppercase font-bold tracking-[0.18em] text-[color:var(--sg-muted)] bg-[color:var(--sg-card)]/95 backdrop-blur-sm"
              >
                {section.label}
              </div>
              <ul className="list-none px-2 pb-2 space-y-0.5">
                {visible.map((item) => (
                  <li key={item.to}>
                    <NavRow
                      item={item}
                      onNavigate={onNavigate}
                      sectionColor={section.color}
                    />
                  </li>
                ))}
              </ul>
            </React.Fragment>
          );
        })}
      </nav>
    );
  },
);
NavList.displayName = "NavList";

/**
 * A single row in the sidebar nav. Handles both the expanded (icon + label
 * with left-edge active bar) and collapsed (icon-only, centred, with Radix
 * Tooltip on hover) variants, plus external links.
 */
const NavRow: React.FC<{
  item: NavItem;
  collapsed?: boolean;
  onNavigate?: () => void;
  sectionColor?: NavSection["color"];
}> = ({ item, collapsed = false, onNavigate, sectionColor }) => {
  const Icon = item.icon;
  const iconTint = sectionColor ? SECTION_ICON_COLOR[sectionColor] : "text-[color:var(--sg-muted)]";

  const collapsedCls =
    "sg-nav-link relative flex items-center justify-center h-6 w-6 mx-auto rounded-md text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)] transition-colors";
  const expandedBase =
    "sg-nav-link relative flex items-center gap-3 pl-4 pr-3 py-1.5 rounded-md text-[13px] font-medium leading-tight transition-colors";

  const buildContent = () => {
    if (item.external) {
      return (
        <a
          href={item.to}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          data-testid={item.tid}
          className={
            collapsed
              ? collapsedCls
              : `${expandedBase} text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)]`
          }
        >
          <Icon
            className={`${collapsed ? "h-5 w-5" : "h-4 w-4"} shrink-0 ${iconTint}`}
            strokeWidth={2}
          />
          {!collapsed && (
            <>
              <span className="flex-1 truncate">{item.label}</span>
              <ExternalLink className="h-3 w-3 text-[color:var(--sg-muted)]" strokeWidth={2} />
            </>
          )}
        </a>
      );
    }
    return (
      <NavLink
        to={item.to}
        onClick={onNavigate}
        data-testid={item.tid}
        className={({ isActive }) => {
          if (collapsed) {
            return `${collapsedCls} ${
              isActive
                ? "bg-[color:var(--sb-saffron,#F97316)]/10 text-[color:var(--sb-saffron,#F97316)]"
                : ""
            }`;
          }
          return `${expandedBase} ${
            isActive
              ? "bg-[color:var(--sb-saffron,#F97316)]/10 text-[color:var(--sb-saffron,#F97316)] font-semibold before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[2px] before:rounded-r before:bg-[color:var(--sb-saffron,#F97316)]"
              : "text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)]"
          }`;
        }}
      >
        {({ isActive }) => (
          <>
            <Icon
              className={`${collapsed ? "h-5 w-5" : "h-4 w-4"} shrink-0 ${isActive ? "" : iconTint}`}
              strokeWidth={isActive ? 2.5 : 2}
            />
            {!collapsed && (
              <>
                <span className="flex-1 truncate">{item.label}</span>
                {item.attention && (
                  <AttentionDot color="bg-fuchsia-500" size={1.5} testId={`${item.tid}-attn`} />
                )}
              </>
            )}
            {collapsed && item.attention && (
              <span
                aria-hidden
                data-testid={`${item.tid}-attn`}
                className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-fuchsia-500"
              />
            )}
          </>
        )}
      </NavLink>
    );
  };

  // Collapsed rail: wrap the row in a Radix Tooltip that reveals the label
  // on hover (world-class rail UX — Vercel, Linear, Notion all do this).
  if (collapsed) {
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>{buildContent()}</TooltipTrigger>
        <TooltipContent
          side="right"
          sideOffset={8}
          className="sb-nav-tooltip font-medium shadow-lg"
        >
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }
  return buildContent();
};

export const Sidebar: React.FC = () => {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <aside
      data-testid="app-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      /* Outer <aside> stretches to the parent flex row's full height so the
         column background fills all the way down even when the main content
         is longer than the viewport. The inner <div> is sticky+h-screen so
         the nav pane stays pinned as the page scrolls; only the middle nav
         region scrolls internally. Width toggles between full and rail. */
      className={`hidden md:block shrink-0 border-r border-[color:var(--sg-border)] bg-[color:var(--sg-card)] self-stretch transition-[width] duration-200 ease-out ${
        collapsed ? "w-16" : "w-60"
      }`}
    >
      <TooltipProvider delayDuration={200} skipDelayDuration={0}>
        <div className={`flex flex-col ${collapsed ? "sticky top-0 h-screen" : "min-h-screen"}`}>
        <SidebarNavContent collapsed={collapsed} />
        {/* Collapse toggle — anchored at the very bottom of the sidebar. */}
        <div className="border-t border-[color:var(--sg-border)] shrink-0 bg-[color:var(--sg-card)]">
          <button
            type="button"
            onClick={() => setCollapsed()}
            data-testid="sidebar-collapse-toggle"
            aria-label={collapsed ? "Expand sidebar (Ctrl+B)" : "Collapse sidebar (Ctrl+B)"}
            aria-expanded={!collapsed}
            title={collapsed ? "Expand · Ctrl+B" : "Collapse · Ctrl+B"}
            className={`w-full flex items-center gap-2 text-xs text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)] transition-colors ${
              collapsed ? "justify-center py-2.5" : "justify-between px-4 py-2.5"
            }`}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" strokeWidth={2} />
            ) : (
              <>
                <span className="inline-flex items-center gap-2">
                  <PanelLeftClose className="h-4 w-4" strokeWidth={2} />
                  Collapse
                </span>
                <kbd className="text-[9px] font-mono bg-[color:var(--sg-muted-bg)] border border-[color:var(--sg-border)] rounded px-1 py-0.5">
                  {isMac ? "⌘B" : "Ctrl+B"}
                </kbd>
              </>
            )}
          </button>
        </div>
        </div>
      </TooltipProvider>
    </aside>
  );
};
